/**
 * ImageCropModal - Crop editor with preset aspect ratios for FB/IG
 * Uses react-easy-crop
 */
import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { X, Crop, Check, Facebook, Instagram } from 'lucide-react';

const PRESETS = [
  { label: '1:1', aspect: 1, platform: 'fb', desc: 'FB / IG post' },
  { label: '4:5', aspect: 4 / 5, platform: 'ig', desc: 'IG / FB mobile' },
  { label: '16:9', aspect: 16 / 9, platform: 'fb', desc: 'FB link / cover' },
  { label: '9:16', aspect: 9 / 16, platform: 'ig', desc: 'IG Reels / Story' },
];

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, pixelCrop.width, pixelCrop.height
  );
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(URL.createObjectURL(blob));
    }, 'image/jpeg', 0.92);
  });
}

export default function ImageCropModal({ imageUrl, onSave, onClose }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(1);
  const [activePreset, setActivePreset] = useState('1:1');
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      const croppedUrl = await getCroppedImg(imageUrl, croppedAreaPixels);
      onSave(croppedUrl);
    } catch {
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="crop-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'var(--apisai-border)' }}>
          <div className="flex items-center gap-2">
            <Crop className="w-4 h-4" style={{ color: 'var(--apisai-primary)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--apisai-text)' }}>Kadruj zdjęcie</span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded-lg">
            <X className="w-4 h-4 text-zinc-500" />
          </button>
        </div>

        {/* Crop Area */}
        <div className="relative w-full" style={{ height: '380px', background: '#1a1a1a' }}>
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        {/* Zoom slider */}
        <div className="px-5 py-3 flex items-center gap-3 border-t" style={{ borderColor: 'var(--apisai-border)' }}>
          <span className="text-xs" style={{ color: 'var(--apisai-text-muted)' }}>Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 h-1.5 accent-orange-500"
            data-testid="crop-zoom-slider"
          />
        </div>

        {/* Preset Buttons */}
        <div className="px-5 py-3 border-t" style={{ borderColor: 'var(--apisai-border)' }}>
          <p className="text-xs font-medium mb-2" style={{ color: 'var(--apisai-text-muted)' }}>Format</p>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => {
              const isActive = activePreset === p.label;
              const PlatformIcon = p.platform === 'fb' ? Facebook : Instagram;
              return (
                <button
                  key={p.label}
                  onClick={() => { setAspect(p.aspect); setActivePreset(p.label); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all"
                  style={{
                    background: isActive ? (p.platform === 'fb' ? '#EBF5FF' : '#FDF2F8') : 'white',
                    borderColor: isActive ? (p.platform === 'fb' ? '#1877F2' : '#E1306C') : 'var(--apisai-border)',
                    color: isActive ? (p.platform === 'fb' ? '#1877F2' : '#E1306C') : 'var(--apisai-text-secondary)',
                  }}
                  data-testid={`crop-preset-${p.label.replace(':', '-')}`}
                >
                  <PlatformIcon className="w-3 h-3" />
                  {p.label}
                  <span className="opacity-60">{p.desc}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t" style={{ borderColor: 'var(--apisai-border)' }}>
          <Button variant="outline" onClick={onClose} className="rounded-xl h-9 text-xs px-4" data-testid="crop-cancel-btn">
            Anuluj
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="rounded-xl h-9 text-xs px-4 text-white font-medium"
            style={{ background: 'var(--apisai-primary)' }}
            data-testid="crop-save-btn"
          >
            {saving ? 'Zapisuję...' : <><Check className="w-3.5 h-3.5 mr-1" /> Zastosuj</>}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
